import type { Group, Object3D } from 'three';
import { Vector3 } from 'three';

import type { HighlightManager } from '../../../three-helpers/highlight';
import type {
	CameraManager,
	DebugManager,
	GeoLightingManager,
	SceneManager
} from '../../managers';
import type { SceneComposition } from '../../types';
import type { SceneCameraDelegate } from '../delegates/scene-camera-delegate.class';
import type { SceneTextureDelegate } from '../delegates/scene-texture-delegate.class';
import { boundsToVectors, calculateSceneBoundsMetrics } from './scene-bounds.cfn';

// ========== applyCompositionCfn ==========

export type ApplyCompositionParams = {
	composition: SceneComposition;
	layer?: string;
	sceneManager: SceneManager | null;
	cameraManager: CameraManager | null;
	cameraDelegate: SceneCameraDelegate | null;
	highlightManager: HighlightManager | null;
	geoLightingManager: GeoLightingManager | null;
	debugManager: DebugManager | null;
	debugMode: boolean;
	textureDelegate: SceneTextureDelegate | null;
	removeFoundationFloor: () => void;
};

/**
 * Frame the scene up front when the delegate is configured to 'settle'.
 *
 * Runs before the composition's objects are added, so the scene appears already
 * framed instead of arriving under whatever camera the foundation left behind.
 */
const settleFramingIfConfigured = (
	cameraDelegate: SceneCameraDelegate | null,
	bounds: SceneComposition['bounds']
): void => {
	if (!bounds || !cameraDelegate?.isAutoZoomEnabled) return;
	if (cameraDelegate.autoZoomStyle !== 'settle') return;
	cameraDelegate.settleAutoZoom(bounds);
};

/**
 * Add composition objects to the scene; position camera from bounds or config.
 */
export const applyCompositionCfn = async (params: ApplyCompositionParams): Promise<void> => {
	const {
		composition, layer, sceneManager, cameraManager, cameraDelegate,
		highlightManager, geoLightingManager, debugManager, debugMode,
		removeFoundationFloor
	} = params;

	const objectHolder = sceneManager?.objectHolder;
	if (!objectHolder) return;

	// Skip camera positioning if already pre-positioned by setSceneCenterFromKitchenData
	const cameraAlreadyPositioned = Boolean(cameraDelegate?.defaultCameraPosition && cameraDelegate?.defaultCameraTarget);

	// Always store bounds and highlight circle when available (needed for auto-zoom even when camera is pre-positioned)
	if (composition.bounds) {
		cameraDelegate?.applyBoundsAndHighlight(composition.bounds, highlightManager);

		settleFramingIfConfigured(cameraDelegate, composition.bounds);
	}

	// Position camera BEFORE adding objects to avoid visible transition
	if (composition.bounds && cameraManager && !cameraAlreadyPositioned) {
		cameraDelegate?.calculateCameraFromBounds(composition.bounds, false);

		const { min, max } = boundsToVectors(composition.bounds);
		const metrics = calculateSceneBoundsMetrics(min, max);

		if (highlightManager) {
			highlightManager.setCircle({
				centerX: metrics.centerX,
				centerZ: metrics.centerZ,
				y: metrics.highlightCircleY,
				radius: metrics.highlightCircleRadius
			});
		}

		// Store default presentation point as fallback (above kitchen center)
		if (cameraDelegate) {
			cameraDelegate.defaultPresentationPoint = new Vector3(metrics.centerX, metrics.highlightCircleY, metrics.centerZ);
		}

		// Update geographic lighting center if enabled
		if (geoLightingManager?.isEnabled) {
			const geoCenter = new Vector3(metrics.centerX, metrics.kitchenCircleY, metrics.centerZ);
			geoLightingManager.setSceneCenter(geoCenter);
		}

		// Create debug circles if in debug mode
		if (debugMode && debugManager) {
			debugManager.addCircle({
				id: 'kitchen-circle',
				center: new Vector3(metrics.centerX, metrics.kitchenCircleY, metrics.centerZ),
				radius: metrics.kitchenCircleRadius,
				color: 0x00ff00
			});

			debugManager.addCircle({
				id: 'highlight-circle',
				center: new Vector3(metrics.centerX, metrics.highlightCircleY, metrics.centerZ),
				radius: metrics.highlightCircleRadius,
				color: 0xffff00
			});

			debugManager.addSphere({
				id: 'scene-center',
				position: new Vector3(metrics.centerX, metrics.kitchenCircleY, metrics.centerZ),
				radius: 1,
				color: 0x00ff00,
				label: 'Scene Center'
			});
		}
	} else if (composition.camera?.position && composition.camera?.target && cameraManager && !cameraAlreadyPositioned) {
		cameraManager.setPosition(
			composition.camera.position.x,
			composition.camera.position.y,
			composition.camera.position.z,
			composition.camera.target.x,
			composition.camera.target.y,
			composition.camera.target.z,
			false
		);
	}

	// Create layer group if specified
	let targetGroup: Group | Object3D = objectHolder;
	if (layer) {
		const existingLayer = objectHolder.getObjectByName(layer) as Group | undefined;
		if (existingLayer) {
			targetGroup = existingLayer;
		} else {
			const { Group: ThreeGroup } = await import('three');
			const layerGroup = new ThreeGroup();
			layerGroup.name = layer;
			objectHolder.add(layerGroup);
			targetGroup = layerGroup;
		}
	}

	// Add objects AFTER camera is positioned
	for (const sceneObject of composition.objects) {
		// Remove foundation floor when builder provides its own
		if (sceneObject.metadata.type === 'floor') {
			removeFoundationFloor();
		}
		sceneObject.object.userData = {
			...sceneObject.object.userData,
			...sceneObject.metadata
		};
		targetGroup.add(sceneObject.object);
	}

	// Set up environment map for reflections on metallic materials
	params.textureDelegate?.ensureEnvironmentMapForReflections();
};

// ========== applyCompositionSettingsCfn ==========

export type ApplyCompositionSettingsParams = {
	composition: SceneComposition;
	cameraManager: CameraManager | null;
	cameraDelegate: SceneCameraDelegate | null;
	highlightManager: HighlightManager | null;
};

/**
 * Apply camera and bounds settings without animation.
 */
export const applyCompositionSettingsCfn = (params: ApplyCompositionSettingsParams): void => {
	const { composition, cameraManager, cameraDelegate, highlightManager } = params;
	if (!cameraManager) return;

	// If camera position was already calculated from kitchen data, use it
	if (cameraDelegate?.defaultCameraPosition && cameraDelegate?.defaultCameraTarget) {
		// Camera is already positioned - just set up highlight circle from bounds
		if (composition.bounds) {
			cameraDelegate.applyBoundsAndHighlight(composition.bounds, highlightManager);
		}
		return;
	}

	// Fallback: calculate camera position from bounds
	if (composition.bounds) {
		cameraDelegate?.calculateCameraFromBounds(composition.bounds, false);

		const { min, max } = boundsToVectors(composition.bounds);
		const metrics = calculateSceneBoundsMetrics(min, max);

		if (highlightManager) {
			highlightManager.setCircle({
				centerX: metrics.centerX,
				centerZ: metrics.centerZ,
				y: metrics.highlightCircleY,
				radius: metrics.highlightCircleRadius
			});
		}
	} else if (composition.camera?.position && composition.camera?.target) {
		cameraManager.setPosition(
			composition.camera.position.x,
			composition.camera.position.y,
			composition.camera.position.z,
			composition.camera.target.x,
			composition.camera.target.y,
			composition.camera.target.z,
			false
		);
	}
};

// ========== applyCompositionSettingsAnimatedCfn ==========

export type ApplyCompositionSettingsAnimatedParams = {
	composition: SceneComposition;
	duration: number;
	cameraManager: CameraManager | null;
	cameraDelegate: SceneCameraDelegate | null;
	highlightManager: HighlightManager | null;
};

/**
 * Apply camera and bounds settings with animated transition.
 */
export const applyCompositionSettingsAnimatedCfn = (params: ApplyCompositionSettingsAnimatedParams): void => {
	const { composition, cameraManager, cameraDelegate, highlightManager } = params;
	if (!cameraManager) return;

	const defaultCamPos = cameraDelegate?.defaultCameraPosition;
	const defaultCamTarget = cameraDelegate?.defaultCameraTarget;

	// If camera position was already calculated from kitchen data, use it
	if (defaultCamPos && defaultCamTarget) {
		cameraManager.setPosition(
			defaultCamPos.x, defaultCamPos.y, defaultCamPos.z,
			defaultCamTarget.x, defaultCamTarget.y, defaultCamTarget.z,
			true
		);

		if (composition.bounds) {
			cameraDelegate!.applyBoundsAndHighlight(composition.bounds, highlightManager);
		}
		return;
	}

	// Fallback: calculate camera position from bounds with animation
	if (composition.bounds) {
		cameraDelegate?.calculateCameraFromBounds(composition.bounds, true);

		const { min, max } = boundsToVectors(composition.bounds);
		const metrics = calculateSceneBoundsMetrics(min, max);

		if (highlightManager) {
			highlightManager.setCircle({
				centerX: metrics.centerX,
				centerZ: metrics.centerZ,
				y: metrics.highlightCircleY,
				radius: metrics.highlightCircleRadius
			});
		}
	} else if (composition.camera) {
		if (composition.camera.position && composition.camera.target) {
			cameraManager.setPosition(
				composition.camera.position.x,
				composition.camera.position.y,
				composition.camera.position.z,
				composition.camera.target.x,
				composition.camera.target.y,
				composition.camera.target.z,
				true
			);
		}
	}
};
